"use client";

import { useWeb3 } from "logic/components";
import { useContractStaticCall, useContractView, useHandleTx } from "logic/hooks";
import { ReserveAccountAbi } from "logic/pages/LoansPage/contracts";
import { toUnits } from "logic/utils";
import { useState } from "react";
import { Address, zeroAddress } from "viem";

interface WithdrawRAButtonProps {
  reserveAccount: Address;
  chainId: number;
}

export function WithdrawRAButton({ reserveAccount, chainId }: WithdrawRAButtonProps) {
  const { address: connectedAddress = zeroAddress } = useWeb3();
  const handleTx = useHandleTx();
  const [status, setStatus] = useState<"idle" | "sending" | "confirming">("idle");

  const { data: reserves } = useContractView({
    address: reserveAccount,
    abi: ReserveAccountAbi,
    functionName: "reserves",
    args: [],
    chainId,
    watch: true,
    query: { enabled: reserveAccount !== zeroAddress },
  });

  const excess = reserves?.[1] ?? 0n;

  const { data: withdrawData } = useContractStaticCall({
    address: reserveAccount,
    abi: ReserveAccountAbi,
    functionName: "withdraw",
    args: [connectedAddress as Address, excess],
    chainId,
    query: {
      enabled: Boolean(excess > 0n && connectedAddress !== zeroAddress),
    },
  });

  const isPending = status === "sending" || status === "confirming";

  async function onClick() {
    if (!withdrawData?.request) return;

    await handleTx({
      title: "Withdraw",
      params: withdrawData.request,
      updateStep: (updates) => {
        if (updates.status === "sending" || updates.status === "confirming") {
          setStatus(updates.status);
        } else {
          setStatus("idle");
        }
      },
    });
  }

  if (excess < toUnits(0.0001) || !withdrawData?.request) return null;

  let text = "Withdraw";
  if (isPending) text = "Withdrawing...";

  return (
    <button
      type="button"
      disabled={!withdrawData?.request || isPending}
      onClick={onClick}
      className="btn-xs btn-primary ml-auto px-8 disabled:opacity-50"
    >
      {text}
    </button>
  );
}
